/**
 * Predictive Search - AquaDropship Theme
 * AJAX-powered search suggestions
 */
(function () {
    let searchInputs = document.querySelectorAll('[data-search-input]');
    if (!searchInputs.length) {
        searchInputs = document.querySelectorAll('form[role=\"search\"] input[type=\"search\"], form[action*=\"/search\"] input[type=\"search\"]');
    }
    if (!searchInputs.length) return;

    const cache = new Map();
    const controllers = new Map();
    const containers = new Map();
    const defaultConfig = {
        loadingText: 'Searching...',
        emptyText: 'No results found',
        viewAllText: 'View all results',
        productsLabel: 'Products',
        collectionsLabel: 'Collections',
        articlesLabel: 'Articles',
        pagesLabel: 'Pages',
        suggestionsLabel: 'Suggestions',
        querySuggestionsLabel: 'Suggested searches',
        recentLabel: 'Recent searches',
        popularLabel: 'Popular searches',
        featuredCollectionsLabel: 'Popular categories',
        limit: 6
    };

    const RECENT_KEY = 'aqua_recent_searches';
    const MAX_RECENT = 6;

    function track(eventName, payload) {
        if (window.dataLayer) {
            window.dataLayer.push({
                event: `search_${eventName}`,
                ...payload
            });
        }
        if (typeof CustomEvent === 'function') {
            document.dispatchEvent(new CustomEvent(`search:${eventName}`, { detail: payload }));
        }
    }

    function escapeHtml(value) {
        return String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function escapeRegExp(value) {
        return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    function highlightText(text, query) {
        const safeText = escapeHtml(text);
        const term = escapeRegExp(query);
        if (!term) return safeText;

        const regex = new RegExp(term, 'ig');
        return safeText.replace(regex, '<mark>$&</mark>');
    }

    function buildProductMeta(product) {
        const vendor = product?.vendor ? escapeHtml(product.vendor) : '';
        const type = product?.product_type ? escapeHtml(product.product_type) : '';
        if (!vendor && !type) return '';
        if (vendor && type) {
            return `${vendor} | ${type}`;
        }
        return vendor || type;
    }

    function safeParseJSON(value, fallback) {
        if (!value) return fallback;
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : fallback;
        } catch (err) {
            return fallback;
        }
    }

    function getRecentSearches() {
        try {
            const stored = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
            if (!Array.isArray(stored)) return [];
            return stored.filter(Boolean).slice(0, MAX_RECENT);
        } catch (err) {
            return [];
        }
    }

    function saveRecentSearch(query) {
        if (!query || query.length < 2) return;
        try {
            const current = getRecentSearches();
            const normalized = query.trim();
            const updated = [normalized, ...current.filter(item => item.toLowerCase() !== normalized.toLowerCase())]
                .slice(0, MAX_RECENT);
            window.localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
        } catch (err) {
            // ignore storage errors
        }
    }

    function getConfig(input) {
        const form = input.closest('form');
        if (!form) return { ...defaultConfig };

        return {
            loadingText: form.dataset.searchLoading || defaultConfig.loadingText,
            emptyText: form.dataset.searchEmpty || defaultConfig.emptyText,
            viewAllText: form.dataset.searchViewAll || defaultConfig.viewAllText,
            productsLabel: form.dataset.searchProducts || defaultConfig.productsLabel,
            collectionsLabel: form.dataset.searchCollections || defaultConfig.collectionsLabel,
            articlesLabel: form.dataset.searchArticles || defaultConfig.articlesLabel,
            pagesLabel: form.dataset.searchPages || defaultConfig.pagesLabel,
            suggestionsLabel: form.dataset.searchSuggestions || defaultConfig.suggestionsLabel,
            querySuggestionsLabel: form.dataset.searchQuerySuggestions || defaultConfig.querySuggestionsLabel,
            recentLabel: form.dataset.searchRecentLabel || defaultConfig.recentLabel,
            popularLabel: form.dataset.searchPopularLabel || defaultConfig.popularLabel,
            featuredCollectionsLabel: form.dataset.searchFeaturedLabel || defaultConfig.featuredCollectionsLabel,
            featuredCollections: safeParseJSON(form.dataset.searchFeaturedCollections, []),
            featuredQueries: safeParseJSON(form.dataset.searchFeaturedQueries, []),
            limit: parseInt(form.dataset.searchSuggestionsLimit || defaultConfig.limit, 10)
        };
    }

    function createContainer(input, config, index) {
        const form = input.closest('form');
        if (!form) return null;
        if (form.dataset.searchEnhanced === 'false') return null;

        const container = document.createElement('div');
        container.className = 'predictive-search';
        container.id = `predictive-search-${index}`;
        container.setAttribute('aria-live', 'polite');
        container.setAttribute('aria-hidden', 'true');
        container.innerHTML = `
      <div class="predictive-search__results">
        <div class="predictive-search__loading" data-predictive-loading style="display: none;">
          <div class="loading-spinner"></div>
          <span>${config.loadingText}</span>
        </div>
        <div data-predictive-results></div>
        <div class="predictive-search__empty" data-predictive-empty style="display: none;">
          <p>${config.emptyText}</p>
        </div>
      </div>
    `;

        form.style.position = 'relative';
        form.appendChild(container);

        return {
            container,
            results: container.querySelector('[data-predictive-results]'),
            loading: container.querySelector('[data-predictive-loading]'),
            empty: container.querySelector('[data-predictive-empty]')
        };
    }

    searchInputs.forEach((input, index) => {
        if (!input.id) {
            input.id = `SearchInput-${index}`;
        }
        const config = getConfig(input);
        const nodes = createContainer(input, config, index);
        if (!nodes) return;

        containers.set(input, { ...nodes, config, lastQuery: '' });

        const form = input.closest('form');
        form?.addEventListener('submit', () => {
            const query = input.value.trim();
            saveRecentSearch(query);
            track('submit', { query });
        });

        let searchTimeout;

        input.addEventListener('input', function () {
            const query = this.value.trim();
            clearTimeout(searchTimeout);

            if (query.length < 2) {
                if (controllers.has(input)) {
                    controllers.get(input).abort();
                }
                if (!renderIdleSuggestions(input)) {
                    hidePredictive(input);
                } else {
                    showPredictive(input);
                }
                return;
            }

            searchTimeout = setTimeout(() => {
                performSearch(input, query);
            }, 200);
        });

        input.addEventListener('focus', function () {
            const query = this.value.trim();
            if (query.length < 2) {
                if (renderIdleSuggestions(input)) {
                    showPredictive(input);
                }
                return;
            }

            const cached = cache.get(query.toLowerCase());
            if (cached) {
                renderResults(input, cached, query);
                showPredictive(input);
            }
        });

        input.addEventListener('keydown', function (event) {
            if (event.key === 'Escape') {
                hidePredictive(input);
            }
        });
    });

    document.addEventListener('click', function (event) {
        if (!event.target.closest('.predictive-search') && !event.target.closest('[data-search-input]')) {
            hideAllPredictive();
        }
    });

    document.addEventListener('click', function (event) {
        const suggestion = event.target.closest('[data-suggestion-query]');
        if (!suggestion) return;

        const inputId = suggestion.getAttribute('data-input-id');
        const input = document.getElementById(inputId);
        if (!input) return;

        const value = suggestion.getAttribute('data-suggestion-query') || '';
        input.value = value;
        saveRecentSearch(value);
        track('suggestion_click', { query: value });
        input.closest('form')?.submit();
    });

    async function performSearch(input, query) {
        const state = containers.get(input);
        if (!state) return;

        const cacheKey = query.toLowerCase();
        const cached = cache.get(cacheKey);
        if (cached) {
            renderResults(input, cached, query);
            showPredictive(input);
            track('suggestions_cached', { query });
            return;
        }

        showPredictive(input);
        state.loading.style.display = 'flex';
        state.empty.style.display = 'none';
        state.results.innerHTML = '';
        state.lastQuery = query;

        if (controllers.has(input)) {
            controllers.get(input).abort();
        }
        const controller = new AbortController();
        controllers.set(input, controller);

        const resourceLimit = Number.isNaN(state.config.limit) ? defaultConfig.limit : state.config.limit;
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article,query&resources[limit]=${resourceLimit}&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,vendor`;

        try {
            const response = await fetch(url, { signal: controller.signal });
            if (!response.ok) {
                throw new Error(`Search request failed: ${response.status}`);
            }
            const data = await response.json();
            cache.set(cacheKey, data);
            renderResults(input, data, query);
            track('suggestions_loaded', { query });
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Search error:', err);
                track('suggestions_error', { query });
                if (state.empty) {
                    state.empty.style.display = 'flex';
                }
            }
        } finally {
            if (state.loading) {
                state.loading.style.display = 'none';
            }
        }
    }

    function renderResults(input, data, query) {
        const state = containers.get(input);
        if (!state) return;

        const products = data.resources?.results?.products || [];
        const articles = data.resources?.results?.articles || [];
        const collections = data.resources?.results?.collections || [];
        const queries = data.resources?.results?.queries || [];

        const hasResults = products.length || articles.length || collections.length || queries.length;
        if (!hasResults) {
            state.empty.style.display = 'flex';
            state.results.innerHTML = '';
            track('suggestions_empty', { query });
            return;
        }

        let html = '';

        if (queries.length > 0) {
            html += `<div class="predictive-search__group predictive-search__group--queries">
        <h3 class="predictive-search__group-title">${state.config.querySuggestionsLabel}</h3>`;
            queries.forEach(suggestion => {
                const label = suggestion.text || suggestion.query || '';
                if (!label) return;
                const highlightedLabel = highlightText(label, query);
                const safeLabel = escapeHtml(label);
                html += `
          <button type="button" class="predictive-search__item predictive-search__item--query" data-suggestion-query="${safeLabel}" data-input-id="${input.id}">
            <span class="predictive-search__item-title">${highlightedLabel}</span>
          </button>`;
            });
            html += '</div>';
        }

        if (products.length > 0) {
            html += `<div class="predictive-search__group">
        <h3 class="predictive-search__group-title">${state.config.productsLabel}</h3>`;
            products.forEach(product => {
                const productTitle = highlightText(product.title, query);
                const meta = buildProductMeta(product);
                html += `
          <a href="${product.url}" class="predictive-search__item">
            ${product.image ? `<img src="${product.image}" alt="${escapeHtml(product.title)}" width="56" height="56">` : ''}
            <div class="predictive-search__item-info">
              <span class="predictive-search__item-title">${productTitle}</span>
              ${meta ? `<span class="predictive-search__item-meta">${meta}</span>` : ''}
              <span class="predictive-search__item-price">${formatMoney(product.price)}</span>
            </div>
          </a>`;
            });
            html += '</div>';
        }

        if (collections.length > 0) {
            html += `<div class="predictive-search__group">
        <h3 class="predictive-search__group-title">${state.config.collectionsLabel}</h3>`;
            collections.forEach(collection => {
                const collectionTitle = highlightText(collection.title, query);
                html += `
          <a href="${collection.url}" class="predictive-search__item">
            <div class="predictive-search__item-info">
              <span class="predictive-search__item-title">${collectionTitle}</span>
            </div>
          </a>`;
            });
            html += '</div>';
        }

        if (articles.length > 0) {
            html += `<div class="predictive-search__group">
        <h3 class="predictive-search__group-title">${state.config.articlesLabel}</h3>`;
            articles.forEach(article => {
                const articleTitle = highlightText(article.title, query);
                html += `
          <a href="${article.url}" class="predictive-search__item">
            <div class="predictive-search__item-info">
              <span class="predictive-search__item-title">${articleTitle}</span>
            </div>
          </a>`;
            });
            html += '</div>';
        }

        html += `<a href="/search?q=${encodeURIComponent(query)}" class="predictive-search__view-all btn btn--primary btn--sm btn--full">
      ${state.config.viewAllText}
    </a>`;

        state.results.innerHTML = html;
        state.empty.style.display = 'none';
        track('suggestions_rendered', {
            query,
            products: products.length,
            collections: collections.length,
            articles: articles.length,
            queries: queries.length
        });
    }

    function renderIdleSuggestions(input) {
        const state = containers.get(input);
        if (!state) return false;

        const recent = getRecentSearches();
        const featuredQueries = state.config.featuredQueries || [];
        const featuredCollections = state.config.featuredCollections || [];
        let html = '';

        state.loading.style.display = 'none';
        if (recent.length > 0) {
            html += `<div class="predictive-search__group">
        <h3 class="predictive-search__group-title">${state.config.recentLabel}</h3>`;
            recent.forEach(item => {
                const safeItem = escapeHtml(item);
                html += `
          <button type="button" class="predictive-search__item predictive-search__item--query" data-suggestion-query="${safeItem}" data-input-id="${input.id}">
            <span class="predictive-search__item-title">${safeItem}</span>
          </button>`;
            });
            html += '</div>';
        }

        if (featuredQueries.length > 0) {
            html += `<div class="predictive-search__group">
        <h3 class="predictive-search__group-title">${state.config.popularLabel}</h3>`;
            featuredQueries.forEach(item => {
                const safeItem = escapeHtml(item);
                html += `
          <button type="button" class="predictive-search__item predictive-search__item--query" data-suggestion-query="${safeItem}" data-input-id="${input.id}">
            <span class="predictive-search__item-title">${safeItem}</span>
          </button>`;
            });
            html += '</div>';
        }

        if (featuredCollections.length > 0) {
            html += `<div class="predictive-search__group">
        <h3 class="predictive-search__group-title">${state.config.featuredCollectionsLabel}</h3>`;
            featuredCollections.forEach(item => {
                const title = escapeHtml(item.title);
                const url = escapeHtml(item.url);
                html += `
          <a href="${url}" class="predictive-search__item">
            <div class="predictive-search__item-info">
              <span class="predictive-search__item-title">${title}</span>
            </div>
          </a>`;
            });
            html += '</div>';
        }

        if (!html) return false;

        state.results.innerHTML = html;
        state.empty.style.display = 'none';
        return true;
    }

    function showPredictive(input) {
        const state = containers.get(input);
        if (!state) return;
        state.container.setAttribute('aria-hidden', 'false');
        state.container.classList.add('is-open');
    }

    function hidePredictive(input) {
        const state = containers.get(input);
        if (!state) return;
        state.container.setAttribute('aria-hidden', 'true');
        state.container.classList.remove('is-open');
    }

    function hideAllPredictive() {
        containers.forEach((_, input) => hidePredictive(input));
    }

    window.addEventListener('storage', (event) => {
        if (event.key === RECENT_KEY) {
            containers.forEach((_, input) => {
                if (document.activeElement === input && input.value.trim().length < 2) {
                    if (renderIdleSuggestions(input)) {
                        showPredictive(input);
                    }
                }
            });
        }
    });

    function formatMoney(cents) {
        const currency = window.Shopify?.currency?.active || 'USD';
        const locale = document.documentElement.lang || 'en-US';
        const value = Number.isFinite(cents) ? cents : parseFloat(cents || 0);
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency
        }).format(value / 100);
    }
})();
